import { useMutation } from '@tanstack/react-query';
import React, { createRef, useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Icon, Input, PasswordInput, Table, TableColumn, TableVariant, Tooltip } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { SectionMfaSms } from '~/app/pages/User/Personal/SectionMfaSms';
import { SectionMfaTotp } from '~/app/pages/User/Personal/SectionMfaTotp';
import { AddPasskeyButton, SectionPasskeys } from '~/app/pages/User/Personal/SectionPasskeys';
import { SectionResetPassword } from '~/app/pages/User/Personal/SectionResetPassword';
import { CopyInput, FormField, Notification, RenderFields, SectionHeader } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { PendingIcon, PlaneButton } from '~/elements';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { isAllowedAppAccess } from '~/lib/session/isAllowedAppAccess';
import { UserModel } from '~/models';
import { DashboardUser, PlatformRole, UserRepository } from '~/repositories';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FieldStatus, FormElementAction, FormFieldStatus, FormFieldStatusWrapper, Inner, Text } from '~/styles';
import { log } from '~/util';

interface CustomDashboardUser extends DashboardUser {
  temporaryData?: {
    authenticatorApp?: string;
    changePassword?: string;
    currentPassword?: string;
  };
}

const Resend = styled(Button)`
  min-width: 108px;
`;

const Action = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// Smoothly reveals the Current Password field below Change Password. The 24px field gap lives inside
// the reveal (CurrentPasswordInner) so it collapses along with the field instead of leaving a gutter.
// visibility takes the collapsed field out of the tab order / a11y tree (not just visually clipped).
const CurrentPasswordReveal = styled.div<{ $show: boolean }>`
  display: grid;
  grid-template-rows: ${({ $show }) => ($show ? '1fr' : '0fr')};
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  transition:
    grid-template-rows 0.25s ease,
    visibility 0.25s ease;
`;

const CurrentPasswordRevealInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const CurrentPasswordInner = styled.div`
  padding-top: 24px;
`;

// Suppress the blue autofill highlight 1Password/Chrome paint onto the input when a value is filled.
const CurrentPasswordInput = styled(PasswordInput)`
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active,
  input:autofill {
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.style.inputDefaultBackgroundColor} inset !important;
    box-shadow: 0 0 0 1000px ${({ theme }) => theme.style.inputDefaultBackgroundColor} inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.style.inputDefaultTextColor} !important;
    caret-color: ${({ theme }) => theme.primary.background};
    transition: background-color 9999s ease-in-out 0s;
  }
`;

const StyledEmailInput = styled(Input)`
  padding-right: 124px;
`;

const RoleHelpButton = styled(Button)`
  padding: 0;
`;

const StyledTooltip = styled(Tooltip)``;

const LeaveButton = styled(Button)`
  padding: ${({ theme }) => theme.style.buttonSmallPadding};
`;

interface PlatformRow {
  id: string;
  platformName: string;
  roleName: string;
  platformId: string;
  isOwner: boolean;
  isLiveEnabled: boolean;
}

export const PagePersonal: React.FC = () => {
  const {
    currentPlatform,
    currentUser,
    refetchCurrentUser,
    setCurrentUser,
    setPlatform,
    loadPlatform,
    loginAuthMethod,
    mfaStatus,
  } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const deviceMFAEnabled = useFeatureFlag(FeatureFlag.EnableDeviceMFA);
  const passkeyAuthEnabled = useFeatureFlag(FeatureFlag.EnablePasskeyAuth);
  const featureFlagRequireLiveVerification = useFeatureFlag(FeatureFlag.EnableLiveModeMfaRequired);
  const notificationContainer = createRef<HTMLDivElement>();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const executeReCAPTCHA = useReCAPTCHA();

  const formMethods = useForm<CustomDashboardUser>({
    defaultValues: {
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
      phoneNumber: currentUser?.phoneNumber,
      email: currentUser?.email,
    },
  });

  const { handleSubmit, formState, reset } = formMethods;
  const { isDirty } = formState;

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  // Bumped after a successful password change to remount the password inputs, discarding the autofill
  // highlight 1Password leaves on the DOM node (clearing the value alone doesn't remove it).
  const [passwordResetKey, setPasswordResetKey] = useState<number>(0);

  // The header/footer "Save changes" buttons stay disabled until there's something to save: a changed
  // profile field (react-hook-form dirty state) or a new password entered.
  const canSave = isDirty || newPassword.length > 0;

  if (!currentUser) {
    return null;
  }

  const handleVerifyEmail = async (resend?: boolean) => {
    await currentUser.createVerifyEmail();
    log({
      name: `User email verify${resend ? ' resent' : ''}`,
      context: currentUser,
    });

    if (resend) {
      addSuccessNotification({
        content: 'Verification email resent! Check your inbox.',
      });
    }
  };

  // Clearing the New Password collapses the confirm/current fields, so wipe their values too rather
  // than leaving stale entries hidden in state.
  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    if (value.length === 0) {
      setConfirmPassword('');
      setCurrentPassword('');
    }
  };

  const handleFormError = (errors: FieldErrors<CustomDashboardUser>) => {
    Object.values(errors).forEach((error) => {
      if (error.message) {
        addDangerNotification({
          content: error.message,
        });
      }
    });
  };

  const handleSave = (data: CustomDashboardUser) => {
    if (isDirty) {
      executeReCAPTCHA(RecaptchaAction.UpdateUser)
        .then((reCaptchaToken) =>
          currentUser
            .update(
              {
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
              },
              reCaptchaToken
            )
            .then(() => {
              addSuccessNotification({
                content: 'User data updated',
              });
              // Rebase the form's dirty baseline onto the saved values so "Save changes" disables again.
              reset(data);
            })
            .catch((error) => {
              addDangerNotification({
                content: error.message,
              });
            })
        )
        .catch((err: Error) => {
          addDangerNotification({
            content: err.message,
          });
        });
    }

    if (newPassword) {
      if (!currentPassword) {
        addDangerNotification({
          content: 'Please enter your current password',
        });
        return;
      }

      if (newPassword !== confirmPassword) {
        addDangerNotification({
          content: 'Passwords are not the same',
        });
        return;
      }

      // The password endpoint is step-up gated, so the client interceptor launches the 2FA
      // confirmation modal and retries this request once the challenge succeeds.
      UserRepository.updatePassword({ currentPassword, newPassword })
        .then(() => {
          addSuccessNotification({
            content: 'Password changed',
          });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordResetKey((key) => key + 1);
        })
        .catch((error) => {
          // The user dismissed the 2FA modal: swallow the re-surfaced step-up error.
          if (isStepUpRequiredError(error)) {
            return;
          }
          addDangerNotification({
            content: error.message,
          });
        });
    }
  };

  const leavePlatformMutation = useMutation({
    mutationFn: async ({ platformId }: { platformId: string }) => {
      if (!currentUser) throw new Error('No user found');
      return UserRepository.removeUserFromPlatform({
        dashboardUserId: currentUser.id,
        platformId,
      });
    },
    onSuccess: async (_, { platformId }) => {
      const wasCurrentPlatform = currentPlatform?.id === platformId;

      await refetchCurrentUser();

      if (wasCurrentPlatform) {
        const { currentUser: updatedUser } = useSessionStore.getState();
        if (!updatedUser) {
          addDangerNotification({
            content: 'Failed to refresh user data',
          });
          return;
        }

        const ownerPlatform = updatedUser.platformRoles?.find((role: PlatformRole) => role.name === 'owner');

        if (ownerPlatform) {
          const updatedUserModel = new UserModel({
            ...updatedUser,
            defaultPlatformId: ownerPlatform.platformId,
          });

          const newPlatformRole = updatedUser.platformRoles?.find(
            (role: PlatformRole) => role.platformId === ownerPlatform.platformId
          );

          if (newPlatformRole) {
            flushSync(() => {
              setCurrentUser(updatedUserModel);
              setPlatform(undefined, newPlatformRole);
            });

            loadPlatform({ user: updatedUserModel })
              .then(() =>
                executeReCAPTCHA(RecaptchaAction.UpdateUser)
                  .then((reCaptchaToken) =>
                    updatedUserModel
                      .update({ defaultPlatformId: ownerPlatform.platformId }, reCaptchaToken)
                      .then(() => {
                        log({
                          name: 'Platform changed after leaving',
                          context: updatedUserModel,
                        });
                      })
                      .catch((error) => {
                        addDangerNotification({
                          content: error.message,
                        });
                      })
                  )
                  .catch((err: Error) => {
                    addDangerNotification({
                      content: err.message,
                    });
                  })
              )
              .catch((error) => {
                addDangerNotification({
                  content: error.message || 'Failed to switch platform',
                });
              });
          }
        }
      }

      addSuccessNotification({
        content: 'Successfully left platform',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to leave platform',
      });
    },
  });

  const handleLeavePlatform = useCallback(
    (platformName: string, platformId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      openAlert({
        headline: `Leave ${platformName}?`,
        content: `You will lose all access to this platform and its associated data. Are you sure you want to leave ${platformName}?`,
        confirmButton: {
          label: 'Leave Platform',
          variant: 'danger',
        },
        onSuccess: () => {
          leavePlatformMutation.mutate({ platformId });
        },
      });
    },
    [openAlert, leavePlatformMutation]
  );

  const platformList: PlatformRow[] = useMemo(() => {
    if (!currentUser?.platformRoles) return [];
    return currentUser.platformRoles.map((platformRole) => ({
      id: platformRole.platformId,
      platformName: platformRole.platformName || 'Unknown Platform',
      roleName: platformRole.name,
      platformId: platformRole.platformId,
      isOwner: platformRole.name === 'owner',
      isLiveEnabled: platformRole.isLiveEnabled,
    }));
  }, [currentUser?.platformRoles]);

  const platformColumns: TableColumn<PlatformRow>[] = useMemo(
    () => [
      {
        header: 'Platform Name',
        accessor: 'platformName',
        width: { min: 300, max: '1fr' },
        cell: ({ value }) => (
          <Table.Cell>
            <Text size={14} weight="medium">
              {value}
            </Text>
          </Table.Cell>
        ),
      },
      {
        header: (
          <RoleHelpButton
            icon={<Icon.CircleQuestionmark />}
            onClick={() => openHelpSidebar('Roles')}
            variant="subtle"
            size="small"
          >
            Role
          </RoleHelpButton>
        ),
        accessor: 'roleName',
        width: { min: 'min-content', max: 'min-content' },
        cell: ({ value }) => <Table.ChipCell value={getRoleLabel(value)} variant="subtle" size="large" />,
      },
      {
        header: 'Platform ID',
        accessor: 'platformId',
        width: { min: 312, max: 330 },
        cell: ({ value }) => (
          <Table.Cell>
            <CopyInput value={value} size="small" isMonospaced variant="text-only" fullWidth />
          </Table.Cell>
        ),
      },
    ],
    [openHelpSidebar]
  );

  return (
    <>
      <PageActions>
        <Button type="button" size="small" isDisabled={!canSave} onClick={handleSubmit(handleSave, handleFormError)}>
          Save changes
        </Button>
      </PageActions>
      {currentPlatform &&
        !isAllowedAppAccess(currentPlatform, mfaStatus, featureFlagRequireLiveVerification, loginAuthMethod) && (
          <Inner pb={0}>
            <Notification color="danger" variant="light" withClose={false}>
              Additional verification is required to access this platform. Please enable two-factor authentication.
            </Notification>
          </Inner>
        )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleSave, handleFormError)}>
          {RenderFields<CustomDashboardUser>({
            sections: [
              {
                headline: 'Personal Information',
                fields: [
                  {
                    id: 'firstName',
                    label: 'First Name',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <Input
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="First Name"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'lastName',
                    label: 'Last Name',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                      return (
                        <Input
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          ref={ref}
                          placeholder="Last Name"
                          hasError={isTouched && !!error}
                        />
                      );
                    },
                  },
                  {
                    id: 'email',
                    label: 'Email address',
                    children: ({ value }, { isTouched, error }) => {
                      const isEmailVerified = value === currentUser.email && currentUser.emailState === 'VERIFIED';
                      const isEmailPending =
                        value === currentUser.email && currentUser.emailState === 'VERIFY_IN_PROGRESS';

                      return (
                        <FormElementAction gap={isEmailVerified ? 0 : undefined}>
                          <FormFieldStatusWrapper>
                            <StyledEmailInput
                              value={String(value)}
                              placeholder="Email Address"
                              autoComplete="off"
                              hasError={isTouched && !!error}
                              isDisabled
                            />

                            <FormFieldStatus ref={notificationContainer}>
                              <FieldStatus show={isEmailPending} color="pending">
                                <StyledTooltip
                                  content="A verification email has been sent. Please check your inbox to complete verification."
                                  placement="top"
                                >
                                  Pending
                                </StyledTooltip>
                                <PendingIcon />
                              </FieldStatus>

                              <FieldStatus show={isEmailVerified} color="success">
                                Verified <Icon.Checkmark />
                              </FieldStatus>
                              <FieldStatus show={!isEmailPending && !isEmailVerified} color="warning">
                                <StyledTooltip
                                  content="Please verify your email address. Verification is required to set up two-factor authentication."
                                  placement="top"
                                >
                                  Not verified
                                </StyledTooltip>
                                <Icon.Warning />
                              </FieldStatus>
                            </FormFieldStatus>
                          </FormFieldStatusWrapper>

                          {!isEmailVerified &&
                            (isEmailPending ? (
                              <Resend
                                type="button"
                                onClick={() => handleVerifyEmail(true)}
                                icon={<Icon.Mail />}
                                variant="secondary"
                              >
                                Resend
                              </Resend>
                            ) : (
                              <PlaneButton
                                onDone={() => handleVerifyEmail()}
                                icon={<Icon.Mail />}
                                success={
                                  <Button
                                    onClick={() => handleVerifyEmail()}
                                    icon={<Icon.Mail />}
                                    variant="secondary"
                                    size="default"
                                  >
                                    Resend
                                  </Button>
                                }
                              >
                                Verify
                              </PlaneButton>
                            ))}
                        </FormElementAction>
                      );
                    },
                  },
                  {
                    content: (
                      <>
                        <FormField<CustomDashboardUser> id="temporaryData.changePassword" label="Update Password">
                          {() => (
                            <SectionResetPassword
                              key={passwordResetKey}
                              newPassword={newPassword}
                              confirmPassword={confirmPassword}
                              onNewPasswordChange={handleNewPasswordChange}
                              onConfirmPasswordChange={setConfirmPassword}
                            />
                          )}
                        </FormField>
                        <CurrentPasswordReveal $show={newPassword.length > 0}>
                          <CurrentPasswordRevealInner>
                            <CurrentPasswordInner>
                              <FormField<CustomDashboardUser>
                                id="temporaryData.currentPassword"
                                label="Current Password"
                              >
                                {() => (
                                  <CurrentPasswordInput
                                    key={passwordResetKey}
                                    placeholder="Current Password"
                                    autoComplete="current-password"
                                    value={currentPassword}
                                    onChange={setCurrentPassword}
                                  />
                                )}
                              </FormField>
                            </CurrentPasswordInner>
                          </CurrentPasswordRevealInner>
                        </CurrentPasswordReveal>
                      </>
                    ),
                  },
                ],
              },
              {
                headline: 'Passkeys',
                description: 'Sign in to your account with secure, one-click authentication.',
                hide: !passkeyAuthEnabled,
                notifications: passkeyAuthEnabled && <AddPasskeyButton hideWhenEmpty />,
                content: passkeyAuthEnabled && <SectionPasskeys />,
              },
              {
                headline: 'Two-factor authentication',
                fields: [
                  {
                    id: 'phoneNumber',
                    label: 'Phone Number',
                    children: ({ onChange }) => {
                      return <SectionMfaSms onPhoneNumberChange={onChange} />;
                    },
                  },
                  {
                    id: 'temporaryData.authenticatorApp',
                    label: 'Authenticator App',
                    hide: !deviceMFAEnabled,
                    children: () => {
                      return deviceMFAEnabled && <SectionMfaTotp />;
                    },
                  },
                ],
              },
            ],
          })}

          <Inner px="var(--page-margin)">
            <Action>
              <Button type="submit" size="small" isDisabled={!canSave}>
                Save changes
              </Button>
            </Action>
          </Inner>
        </form>
      </FormProvider>
      {platformList.length > 0 && (
        <>
          <SectionHeader text="Platforms" border />
          <Inner pt={0} px="var(--page-margin)">
            <Table
              columns={platformColumns}
              data={platformList}
              variant={TableVariant.List}
              getRowId={(row) => row.id}
              rowAction={(row) =>
                !row.isOwner ? (
                  <LeaveButton
                    variant="secondary-danger"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLeavePlatform(row.platformName, row.platformId, e);
                    }}
                  >
                    Leave
                  </LeaveButton>
                ) : null
              }
            />
          </Inner>
        </>
      )}
    </>
  );
};
